import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { LabelChipGrid } from '@/components/ui/LabelChipGrid';
import type { GuestReadingLevel } from '@/store/guest.store';
import type { LabelView } from '@/types/api';

/**
 * The pre-login reading-level picker. Rendered over the blurred guest library
 * (first run) and re-openable to change levels. Multi-select (a book matches
 * if it carries *any* chosen level), with a "browse all levels" escape and a
 * sign-in shortcut for returning users. Mirrors the iOS
 * `ReadingLevelSelectorView`.
 */
export function GuestReadingLevelSelector({
  levels,
  loading = false,
  error = null,
  onRetry,
  initialSelectedIds = [],
  onConfirm,
  onSignIn,
  onClose,
}: {
  levels: LabelView[];
  loading?: boolean;
  /** Set when the reading-level fetch failed; shown with a retry. */
  error?: string | null;
  onRetry?: () => void;
  initialSelectedIds?: string[];
  /** Receives the chosen levels — an empty array means "browse all levels". */
  onConfirm: (levels: GuestReadingLevel[]) => void;
  onSignIn: () => void;
  /** When provided, renders a close (×) button — used when re-opening to edit. */
  onClose?: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelectedIds));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const confirmSelected = () =>
    onConfirm(
      levels.filter((l) => selected.has(l.id)).map((l) => ({ id: l.id, alias: l.alias })),
    );

  return (
    <div className="relative rounded-[28px] border border-white/60 bg-white p-7 shadow-or-pop">
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-or-slate-500 transition hover:bg-or-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}

      <div className="or-kicker">Find the right books</div>
      <h2 className="mt-1 font-display text-[22px] font-bold text-or-slate-900">
        What reading level would you like to browse?
      </h2>
      <p className="mt-1 text-[14px] text-or-slate-600">
        Pick any that fit — you can change this anytime.
      </p>

      <div className="mt-5">
        {loading ? (
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <span
                key={i}
                className="h-8 w-20 animate-pulse rounded-full bg-or-slate-100"
                aria-hidden
              />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-or-rose-100 bg-or-rose-100/40 px-4 py-3 text-[13px] text-or-rose-700">
            <p>{error}</p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 font-semibold text-or-rose-700 underline underline-offset-2 hover:text-or-rose-700/80"
              >
                Try again
              </button>
            ) : null}
          </div>
        ) : (
          <LabelChipGrid
            labels={levels}
            selectedIds={selected}
            onToggle={toggle}
            emptyLabel="No reading levels configured yet."
          />
        )}
      </div>

      <div className="mt-6 flex flex-col gap-2.5">
        <Button size="lg" onClick={confirmSelected} disabled={selected.size === 0} className="w-full">
          Browse these levels
        </Button>
        <button
          type="button"
          onClick={() => onConfirm([])}
          className="text-[13px] font-semibold text-or-slate-600 hover:text-or-orange-700"
        >
          Browse all levels instead
        </button>
      </div>

      <div className="mt-5 border-t border-or-slate-100 pt-4 text-center text-[13px] text-or-slate-500">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSignIn}
          className="font-semibold text-or-orange-700 hover:text-or-orange-800"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}
